import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Field, Form, Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ElementsConsumer,
  CardNumberElement,
} from "@stripe/react-stripe-js";

import { ReactComponent as PayPalWhiteIcon } from "../../icons/payPalIconWhite.svg";
import { PaymentMethod } from "./PaymentMethod";
import BillingAddress from "./BillingAddress";
import RememberMe from "./RememberMe";

import { CartContext } from "../../contexts/CartContext";
import { BusinessContext } from "../../contexts/BusinessContext";
import DeliverySection from "./DeliverySection";
import axios from "axios";

export const PaymentSection = () => {
  const { cart, clearCart, total, shippingCost, taxRate } = useContext(
    CartContext
  );
  const { business, accountDetails } = useContext(BusinessContext);
  const [toastShown, setToastShown] = useState(false);
  const [clientSecret, setClientSecret] = useState("");

  const stripe = loadStripe(
    "pk_live_51NJnQyAZN2FB9ZC3OHdk4biPDQCu73RMn7zo9c1Df7KkC5M261DHqmHnPazfy1oLETNsEz5CcZXDY2OTbFZVnDwg00ydBfbgAd",
    //acct_1NOBJKPEVGdDwtFs
    //"pk_test_51NJnQyAZN2FB9ZC3bOOVa0DFSdMVEb9dRAm6fUtWIescHMtDKFPDWMqX5sir3CIW8Sjw6VcwBQkBM8WLnA2N7AFJ009qVM6zvA",
    {
      stripeAccount: accountDetails?.stripe_account,
      // stripeAccount: "acct_1NOBJKPEVGdDwtFs",
      //acct_1OdgN1PSxmcZAJ0A
    }
  );

  const navigate = useNavigate();

  const initialValues = {
    stripeAccount: accountDetails?.stripe_account,
    email: "",
    delivery: "free",
    firstName: "",
    lastName: "",
    address: "",
    apartment: "",
    city: "",
    state: "alabama",
    zipCode: "",
    phone: "",
    payment: "creditCard",
    allowEmailMarketing: true,
    allowMsgMarketing: true,
    sameShippingAddress: true,
    billing_address_selector: "shippingAddress",
  };
  console.log("?????????????initialValues", initialValues);

  const getFirstSentence = (description) => {
    const firstSentence = description.split(".")[0];
    return firstSentence ? firstSentence + "." : description;
  };
  const calculateSubtotal = useCallback(() => {
    return cart.reduce((total, item) => total + item.price * item.amount, 0);
  }, [cart]);

  const calculateTotal = useCallback(() => {
    const subtotal = calculateSubtotal();
    const taxes = subtotal * taxRate;
    return subtotal + taxes + shippingCost;
  }, [calculateSubtotal, taxRate, shippingCost]);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Enter an email"),
    firstName: Yup.string().required("Enter a first name"),
    lastName: Yup.string().required("Enter a last name"),
    address: Yup.string().required("Enter an address"),
    city: Yup.string().required("Enter a city"),
    zipCode: Yup.string().required("Enter a ZIP / postal code"),
    phone: Yup.string().required("Enter a phone number"),
    nameOnCard: Yup.string().required("Name on card is required"),
  });

  useEffect(() => {
    if (cart.length === 0 && !toastShown) {
      toast.error("Your cart is empty. Please add items to your cart.", {
        duration: 5000,
      });
      setToastShown(true);
      navigate("/");
    }
  }, [cart, navigate, toastShown]);

  useEffect(() => {
    if (accountDetails?.stripe_account) {
      const invoiceTotal = calculateTotal();
      axios
        .post("https://api.octavio.co/payments/charge", {
          amount: invoiceTotal * 100,
          stripe_account_id: accountDetails?.stripe_account,
          currency: "usd",
          invoice_id: "",
        })
        .then((response) => {
          console.log("::::::::::::response", response);
          setClientSecret(response.data.client_secret);
          //setPaymentID(response.data.id);
          console.log("client-secret" + response.data.client_secret);
          //console.log("paymentIntent" + response.data.id);
        });
    }
  }, [accountDetails, calculateTotal]);

  const handleSubmit = async (values, stripe, elements) => {
    if (!stripe || !elements) {
      toast.error("Stripe.js hasn't yet loaded. Please try again.");
      return;
    }

    let errorHandled = false; // Flag to indicate if the error has been handled

    try {
      const customerParams = {
        account_id: business?.account_id,
        customer_balance: 0,
        customer_currency: "USD",
        customer_company_name: "",
        customer_company_phone: values?.phone,
        customer_contact_name: values?.firstName,
        customer_contact_last_name: values?.lastName,
        customer_contact_phone: values?.phone,
        customer_referral_email: values?.email,
        customer_delinquent: false,
        customer_discount: "0",
        customer_email: values.email,
        customer_profile_url_picture: "",
        customer_invoice_settings: {
          footer: "",
        },
        customer_billing: {
          name: `${values.firstName} ${values.lastName}`,
          address_1: values.address,
          address_2: values.apartment,
          city: values.city,
          country_code: "US",
          county: "",
          post_code: values.zipCode,
          state: values.state,
        },
        customer_notes: "",
        customer_tax_pct: taxRate,
      };

      const customerResponse = await fetch("https://api.octavio.co/customers", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(customerParams),
      });

      if (!customerResponse.ok) {
        toast.error("Error creating customer. Please try again.");
        throw new Error(`HTTP error! Status: ${customerResponse.status}`);
      }

      const customer = await customerResponse.json();
      const invoiceSubtotal = calculateSubtotal();
      const invoiceTotal = calculateTotal();

      const invoiceParams = {
        account_id: business?.account_id,
        customer_id: customer?.customer_id,
        invoice_amount_due: parseFloat(total.toFixed(2)),
        invoice_amount_paid: 0,
        invoice_amount_remaining: parseFloat(total.toFixed(2)),
        invoice_attempt_count: 0,
        invoice_attempted: false,
        invoice_status_billing: "unpaid",
        invoice_currency: customer.customer_currency || "USD",
        invoice_date: new Date().toISOString(),
        invoice_discount: "",
        invoice_due_date: new Date().toISOString(),
        invoice_lines: cart.map((item) => ({
          amount: item.price,
          quantity: item.amount,
          description: getFirstSentence(item?.description),
          product_id: item.id,
          plan: {},
        })),
        invoice_next_payment_attempt: new Date().toISOString(),
        invoice_number: "1",
        invoice_paid: false,
        invoice_refunded: false,
        invoice_created: new Date().toISOString(),
        invoice_starting_balance: 0,
        invoice_subtotal: parseFloat(invoiceSubtotal.toFixed(2)),
        invoice_tax_percent: taxRate,
        invoice_total: parseFloat(invoiceTotal.toFixed(2)),
      };

      const invoiceResponse = await fetch(
        "https://api.octavio.co/invoices/web",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(invoiceParams),
        }
      );

      if (!invoiceResponse.ok) {
        toast.error("Error creating the cart order. Please try again.");
        throw new Error(`HTTP error! Status: ${invoiceResponse.status}`);
      }

      const invoice = await invoiceResponse.json();

      const cardElement = elements.getElement(CardNumberElement);
      const {
        error: paymentMethodError,
        paymentMethod,
      } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: values.nameOnCard,
        },
      });

      if (paymentMethodError) {
        toast.error(paymentMethodError.message);
        throw new Error(paymentMethodError.message);
      }

      const { paymentIntent, error } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: paymentMethod.id,
          return_url: "https://www.octavio.co",
        }
      );

      if (error) {
        if (
          error.type === "card_error" &&
          (error.code === "card_declined" ||
            error.code === "insufficient_funds" ||
            error.code === "invalid_cvc")
        ) {
          toast.error(error.message);
        } else {
          toast.error("An error occurred during payment confirmation.");
        }
        errorHandled = true; // Set the flag to true if an error toast was shown
        throw new Error(error.message);
      }
      console.log("====>>>>>paymentIntent", paymentIntent);
      if (paymentIntent && paymentIntent.status === "succeeded") {
        const orderParams = {
          account_id: business?.account_id,
          customer_id: customer.customer_id,
          invoice_id: invoice.invoice_id,
          location_id: "",
          status: "succeeded",
          delivery: values.delivery,
          payment: values.payment,
          stripe_payment_intent: paymentIntent.id,
        };

        const orderResponse = await fetch("https://api.octavio.co/orders/web", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(orderParams),
        });

        if (!orderResponse.ok) {
          toast.error(
            "Error creating the order transaction. Please try again."
          );
          throw new Error(`HTTP error! Status: ${orderResponse.status}`);
        }

        // Update invoice status to "paid"
        const updatedInvoiceParams = {
          ...invoiceParams,
          invoice_status_billing: "paid",
        };

        const updateInvoiceResponse = await fetch(
          `https://api.octavio.co/invoices/web/${invoice.invoice_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatedInvoiceParams),
          }
        );

        if (!updateInvoiceResponse.ok) {
          toast.error("Error updating the invoice status. Please try again.");
          throw new Error(
            `HTTP error! Status: ${updateInvoiceResponse.status}`
          );
        }

        clearCart();
        navigate("/thank-you");
      } else {
        toast.error("Payment was not successful. Please try again.");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      if (!errorHandled) {
        toast.error("There was an error placing your order. Please try again.");
      }
    }
  };

  const options = {
    clientSecret: clientSecret,
    // appearance: "light",
  };

  return clientSecret ? (
    <Elements options={options} stripe={stripe}>
      <ElementsConsumer>
        {({ stripe, elements }) => (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, actions) =>
              handleSubmit(values, stripe, elements)
            }
          >
            {({ isSubmitting, values }) => (
              <Form>
                <div className="w-full">
                  <Link
                    to="/"
                    className="text-mediumGray text-sm underline hover:text-black"
                  >
                    Continue Shopping
                  </Link>
                  <div>
                    <h2 className="text-center text-black text-md">
                      Express checkout
                    </h2>

                    <div className="mb-4">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-3"
                      >
                        <div className="flex justify-between items-center text-black">
                          <span className="text-xl">Contact</span>
                        </div>
                      </label>
                      <Field
                        id="email"
                        type="email"
                        name="email"
                        Placeholder="Your EMAIL"
                        className="mt-1 block w-full px-3 py-4 border border-[#dedede] rounded-md shadow-sm focus:outline focus:ring-black focus:border-black sm:text-sm"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    <div className="text-sm text-black">
                      <label className="cursor-pointer flex gap-2 items-center">
                        <Field
                          type="checkbox"
                          name="allowEmailMarketing"
                          className="h-4 w-4 rounded-lg checkbox-style"
                        />
                        Email me with news and offers
                      </label>
                    </div>
                  </div>
                  <div>
                    <BillingAddress {...{ values }} />
                  </div>

                  <div className="mt-10">
                    <DeliverySection {...{ values }} state={values.state} />
                  </div>

                  <div className="mt-10">
                    <PaymentMethod {...{ values }} />
                  </div>

                  {values.payment === "creditCard" && (
                    <div className="mt-10">
                      <RememberMe {...{ values }} />
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`text-white text-lg font-medium py-3 px-4 rounded w-full mt-4 ${
                    values.payment === "payPal"
                      ? "bg-royalBlue hover:brightness-95"
                      : "bg-navyBlue hover:bg-black"
                  }`}
                >
                  {isSubmitting ? (
                    <ClipLoader size={25} color={"#fff"} />
                  ) : values.payment === "payPal" ? (
                    <div className="flex justify-center items-center gap-2">
                      Pay with <PayPalWhiteIcon className="h-6" />
                    </div>
                  ) : (
                    "Pay now"
                  )}
                </button>
              </Form>
            )}
          </Formik>
        )}
      </ElementsConsumer>
    </Elements>
  ) : (
    <div>
      {" "}
      <ClipLoader size={25} color={"#fff"} />
      Loading...
    </div>
  );
};
